import {
    COMPARATIVE_TESTING_FAQ_ITEMS,
    CONCEPT_TESTING_EXAMPLES_FAQ_ITEMS,
    CONCEPT_TESTING_QUESTIONS_FAQ_ITEMS,
    CONCEPT_TESTING_TOOLS_FAQ_ITEMS,
    CONCEPT_TESTING_VS_USABILITY_TESTING_FAQ_ITEMS,
    HOW_IHUT_IN_HOME_PRODUCT_TESTING_WORKS_FAQ_ITEMS,
    HOW_TO_RECRUIT_REAL_CONSUMERS_TO_TEST_CPG_PRODUCT_IN_HOME_FAQ_ITEMS,
    CLT_VS_IHUT_FAQ_ITEMS,
    FOCUS_GROUPS_VS_SURVEYS_VS_IHUT_FAQ_ITEMS,
    HOW_TO_RUN_AN_IN_HOME_USAGE_TEST_FOR_CPG_PRODUCT_FAQ_ITEMS,
    HOW_MANY_PARTICIPANTS_IHUT_FAQ_ITEMS,
    HOW_TO_INTERPRET_IHUT_REPORTS_FAQ_ITEMS,
    HOW_MUCH_IHUT_COSTS_FAQ_ITEMS,
    HOW_TO_SEND_CPG_PRODUCT_PROTOTYPES_FAQ_ITEMS,
    HOW_TO_TEST_CPG_PRODUCT_WITH_REAL_CONSUMERS_BEFORE_LAUNCH_FAQ_ITEMS,
    IHUT_REPORT_TEMPLATES_FAQ_ITEMS,
    METHODS_IHUT_FAQ_ITEMS,
    TARGETED_CONSUMER_LIST_IHUT_FAQ_ITEMS,
    IHUT_SURVEY_QUESTIONS_FAQ_ITEMS,
    WHAT_CONSUMER_DRIVERS_BRANDS_SHOULD_TEST_WITH_IHUT_FAQ_ITEMS,
    IHUT_FOR_FOOD_AND_BEVERAGE_FAQ_ITEMS,
    IHUT_FOR_PET_PRODUCTS_FAQ_ITEMS,
    IHUT_FOR_REFRIGERATED_FROZEN_PRODUCTS_FAQ_ITEMS,
    IHUT_FOR_BEAUTY_PERSONAL_CARE_FAQ_ITEMS,
    IHUT_FOR_WELLNESS_SUPPLEMENTS_FAQ_ITEMS,
    IHUT_FOR_BABY_PRODUCTS_FAQ_ITEMS,
    IDEA_SCREENING_FAQ_ITEMS,
    KEY_DRIVER_ANALYSIS_FAQ_ITEMS,
    MAXDIFF_ANALYSIS_FAQ_ITEMS,
    MONADIC_TESTING_FAQ_ITEMS,
    PROTOTYPE_TESTING_FAQ_ITEMS,
    WHAT_IS_CLAIMS_TESTING_FAQ_ITEMS,
    WHAT_IS_CONCEPT_TESTING_DEFAULT_FAQ_ITEMS,
    type FaqItem,
} from '~/utils/case-content-faq-items'

export type CaseContentPageConfig = {
    heading?: string
    breadcrumbLabel?: string
    metaTitle?: string
    metaDescription?: string
    bannerImage?: string
    bannerAlt?: string
    listItems?: string[]
    faqItems?: FaqItem[]
    datePublished?: string
    dateModified?: string
    /** When false, the markdown landing page returns 404. Defaults to true. */
    published?: boolean
}

const DEFAULT_CASE_CONTENT_PAGE_CONFIG: CaseContentPageConfig = {
    heading: 'Concept Testing: What, Why, When, and How?',
    breadcrumbLabel: 'What is Concept Testing?',
    bannerImage: 'caseStudies/usecases/conceptTesting/what-is-concept-testing-banner.png',
    listItems: [
        'Written by Milad Zabihi',
        'June 22, 2020',
        '3 min read',
    ],
    faqItems: WHAT_IS_CONCEPT_TESTING_DEFAULT_FAQ_ITEMS,
    published: true,
}

const ROUTE_PAGE_CONFIGS: Record<string, CaseContentPageConfig> = {
    'concept-testing/what-is-concept-testing': {
        ...DEFAULT_CASE_CONTENT_PAGE_CONFIG,
        metaTitle: 'Concept Testing for CPG Products: What, Why, When, and How? | Peekage',
        metaDescription: 'Concept testing prevents costly product failures. Learn how to test product ideas early, choose winners, and launch products consumers actually want.',
    },
    'concept-testing/concept-testing-tools': {
        heading: '8 Concept Testing Tools CPG Product Teams Need',
        breadcrumbLabel: '8 Concept Testing Tools CPG Product Teams Need',
        metaTitle: '8 Concept Testing Tools CPG Product Teams Need | Peekage',
        metaDescription: 'Launching a new product? Compare 8 top concept testing platforms to get real consumer feedback, validate ideas early, and avoid costly failures.',
        bannerImage: 'caseStudies/usecases/conceptTesting/conceptTestingTools/concept-testing-tools-banner.jpg',
        faqItems: CONCEPT_TESTING_TOOLS_FAQ_ITEMS,
    },
    'concept-testing/concept-testing-examples': {
        heading: '5 Concept Testing Examples You Should Know for Better Product Development',
        breadcrumbLabel: 'Concept Testing Examples',
        metaTitle: '5 Concept Testing Examples You Should Know | Peekage',
        metaDescription: 'Learn 5 concept testing examples that help teams validate ideas early, avoid costly mistakes, and build products consumers actually want.',
        bannerImage: 'caseStudies/usecases/conceptTesting/concept-testing-examples/concept-testing-examples-banner.png',
        faqItems: CONCEPT_TESTING_EXAMPLES_FAQ_ITEMS,
    },
    'concept-testing/concept-testing-questions': {
        heading: 'Concept Testing Questions To Ask Your Respondents',
        breadcrumbLabel: 'Concept Testing Questions',
        metaTitle: 'Concept Testing Questions To Ask Your Respondents | Peekage',
        metaDescription: 'Learn which concept testing questions reveal real demand, confusion, and purchase intent with examples to help you avoid costly product mistakes.',
        bannerImage: 'caseStudies/usecases/conceptTesting/concept-testing-questions/concept-testing-question-banner.png',
        faqItems: CONCEPT_TESTING_QUESTIONS_FAQ_ITEMS,
    },
    'concept-testing/monadic-testing': {
        heading: 'How to Use Monadic Concept Testing Method to Validate Product Concepts',
        breadcrumbLabel: 'Monadic Testing',
        metaTitle: 'How to Validate Product Concepts with Monadic Testing | Peekage',
        metaDescription: 'Discover how monadic testing helps you validate your product concepts, avoid costly mistakes, and build products your audience loves.',
        bannerImage: 'caseStudies/usecases/conceptTesting/monadic-testing/monadic-testing-banner.png',
        faqItems: MONADIC_TESTING_FAQ_ITEMS,
    },
    'concept-testing/prototype-testing': {
        heading: 'Prototype Testing: How to Evaluate and Validate Your CPG Product Concept',
        breadcrumbLabel: 'Prototype Testing',
        metaTitle: 'Prototype Testing Methods & Examples for CPG Brands | Peekage',
        metaDescription: 'Learn what prototype testing is, when to use it, and which prototype testing methods work best to validate your concept and build products users love.',
        bannerImage: 'caseStudies/usecases/conceptTesting/prototype-testing/prototype-testing-banner.png',
        faqItems: PROTOTYPE_TESTING_FAQ_ITEMS,
    },
    'concept-testing/key-driver-analysis': {
        heading: 'Key Driver Analysis for CPG Products: Use Cases and Examples',
        breadcrumbLabel: 'Key Driver Analysis',
        metaTitle: 'How to Run Key Driver Analysis for CPG Brands',
        metaDescription: 'Learn how key driver analysis helps CPG brands identify what truly drives purchase decisions, satisfaction, and NPS using real consumer data.',
        bannerImage: 'caseStudies/usecases/conceptTesting/key-driver-analysis/key-driver-analysis-banner.png',
        faqItems: KEY_DRIVER_ANALYSIS_FAQ_ITEMS,
    },
    'concept-testing/concept-testing-vs-usability-testing': {
        heading: 'Concept Testing vs Usability Testing: Differences That Matter for CPG Products',
        breadcrumbLabel: 'Concept Testing vs Usability Testing',
        metaTitle: 'Concept vs Usability Testing: What CPG Brands Need to Know',
        metaDescription: 'Learn the difference between concept testing and usability testing for CPG products, when to use each method, and how they reduce launch risk.',
        bannerImage: 'caseStudies/usecases/conceptTesting/concept-testing-vs-usability-testing/concept-testing-vs-usability-testing-banner.png',
        datePublished: '2020-06-22T00:00:00+00:00',
        dateModified: '2026-07-03T00:00:00+00:00',
        faqItems: CONCEPT_TESTING_VS_USABILITY_TESTING_FAQ_ITEMS,
    },
    'concept-testing/maxdiff-analysis': {
        heading: 'How to Use MaxDiff Analysis for CPG Products to Discover What Consumers Really Want',
        breadcrumbLabel: 'MaxDiff Analysis',
        metaTitle: 'MaxDiff Analysis to Find What Customers Want | Peekage',
        metaDescription: 'Learn what MaxDiff analysis is, when to use it, and how to run a MaxDiff survey to uncover what consumers truly value.',
        bannerImage: 'caseStudies/usecases/conceptTesting/maxdiff-analysis/maxdiff-analysis-banner.png',
        listItems: [
            'Written by Milad Zabihi',
            '10 min read',
        ],
        faqItems: MAXDIFF_ANALYSIS_FAQ_ITEMS,
    },
    'concept-testing/idea-screening': {
        heading: 'Idea Screening for CPG Brands: How to Choose the Perfect Concept for Your Product',
        breadcrumbLabel: 'Idea Screening',
        metaTitle: 'How to Do Idea Screening in New CPG Product Development | Peekage',
        metaDescription: 'Learn how idea screening in new product development helps you filter winning concepts, avoid costly mistakes, and choose ideas with real market potential.',
        bannerImage: 'caseStudies/usecases/conceptTesting/idea-screening/idea-screening-banner.png',
        listItems: [
            'Written by Milad Zabihi',
            '12 min read',
        ],
        faqItems: IDEA_SCREENING_FAQ_ITEMS,
    },
    'concept-testing/comparative-testing': {
        heading: 'How to Do Comparative Concept Testing for CPG Brands',
        breadcrumbLabel: 'Comparative Testing',
        metaTitle: 'Comparative Concept Testing for CPG Brands | Peekage',
        metaDescription: 'Learn how to run comparative concept testing for CPG products. Discover methods, benefits, and how to get actionable insights with Peekage.',
        bannerImage: 'caseStudies/usecases/conceptTesting/comparative-testing/comparative-testing-banner.png',
        listItems: [
            'Written by Milad Zabihi',
            '11 min read',
        ],
        faqItems: COMPARATIVE_TESTING_FAQ_ITEMS,
    },
    'claims-testing/what-is-claims-testing': {
        heading: 'How to Conduct Claims Testing for CPG Brands',
        breadcrumbLabel: 'What is Claims Testing?',
        metaTitle: 'How to Conduct Claims Testing for CPG Brands | Peekage',
        metaDescription: 'Learn how CPG brands can test product claims to boost consumer trust, meet regulations, and see how Peekage makes it easier.',
        bannerImage: 'caseStudies/usecases/claims-testing-services/what-is-claims-testing/what-is-claims-testing-banner.png',
        listItems: [
            'Written by Milad Zabihi',
            '14 min read',
        ],
        faqItems: WHAT_IS_CLAIMS_TESTING_FAQ_ITEMS,
        published: true,
    },
    'in-home-product-testing/how-ihut-in-home-product-testing-works': {
        heading: 'How IHUT Testing Works for CPG Products: A Complete Guide',
        breadcrumbLabel: 'How IHUT In-Home Product Testing Works',
        metaTitle: 'How IHUT Product Testing Works for CPG Products | Peekage',
        metaDescription: 'Learn how IHUT testing helps CPG brands gather real feedback about their products at home. Discover IHUT testing methods, benefits, and best practices.',
        bannerImage: 'caseStudies/usecases/ihut/how-ihut/how-ihut-in-home-product-testing-works-banner.png',
        listItems: [
            'Written by Milad Zabihi',
            '18 min read',
        ],
        faqItems: HOW_IHUT_IN_HOME_PRODUCT_TESTING_WORKS_FAQ_ITEMS,
    },
    'in-home-product-testing/how-to-recruit-real-consumers-to-test-cpg-product-in-home': {
        heading: 'How to Find and Recruit Real Consumers to Test Your CPG Product In-Home',
        breadcrumbLabel: 'How to Recruit Consumers for In-Home Product Testing',
        metaTitle: 'How to Recruit Consumers for In-Home Product Testing | Peekage',
        metaDescription: 'Learn how to find, screen, and recruit the right consumers for in-home product testing, including IHUT sample sizes, recruitment channels, and screening tips.',
        bannerImage: 'caseStudies/usecases/ihut/how-to-recruit/how-to-recruit-real-consumers-to-test-cpg-product-in-home-banner.png',
        listItems: [
            'Written by Milad Zabihi',
            '12 min read',
        ],
        faqItems: HOW_TO_RECRUIT_REAL_CONSUMERS_TO_TEST_CPG_PRODUCT_IN_HOME_FAQ_ITEMS,
    },
    'in-home-product-testing/how-to-run-ihut-for-cpg-product': {
        heading: 'What Is IHUT and How to Run an In-Home Usage Test for Your CPG Product',
        breadcrumbLabel: 'How to Run an In-Home Usage Test for CPG Products',
        metaTitle: 'How to Run an In-Home Usage Test for Your CPG Product | Peekage',
        metaDescription: 'Learn what an in-home usage test (IHUT) is, when to use it, and how to run it. Here you will learn what participant requirements, timelines, costs, and more are.',
        bannerImage: 'caseStudies/usecases/ihut/how-to-run-ihut/how-to-run-ihut-hero-v2.png',
        listItems: [
            'Written by Milad Zabihi',
            '11 min read',
        ],
        faqItems: HOW_TO_RUN_AN_IN_HOME_USAGE_TEST_FOR_CPG_PRODUCT_FAQ_ITEMS,
        published: true,
    },
    'in-home-product-testing/clt-vs-ihut': {
        heading: 'Central Location Test vs IHUT: Choosing Your CPG Method',
        breadcrumbLabel: 'CLT vs IHUT',
        metaTitle: 'CLT vs IHUT: Which One to Choose for Your CPG Product? | Peekage',
        metaDescription: 'Learn the key differences between CLT and IHUT. Find out which CPG product testing method is right for your research goals, budget, timeline, and product.',
        bannerImage: 'caseStudies/usecases/ihut/clt-vs-ihut/clt-vs-ihut-banner-hero-v2.png',
        listItems: [
            'Written by Milad Zabihi',
            '9 min read',
        ],
        faqItems: CLT_VS_IHUT_FAQ_ITEMS,
        published: true,
    },
    'in-home-product-testing/focus-groups-vs-surveys-vs-ihut': {
        heading: 'Focus Groups vs Surveys vs IHUT: The Best CPG Feedback Method for Your Research Objective',
        breadcrumbLabel: 'Focus Groups vs Surveys vs IHUT',
        metaTitle: 'Focus Groups vs Surveys vs IHUT: Which Research Method Is Best?',
        metaDescription: 'Find out when to use focus groups, surveys, or IHUTs for CPG research, based on what you need to learn, your timeline, budget, and research goals.',
        bannerImage: 'caseStudies/usecases/ihut/focus-groups-vs-surveys-vs-ihut/focus-groups-vs-surveys-vs-ihut-hero.png',
        listItems: [
            'Written by Milad Zabihi',
            '12 min read',
        ],
        faqItems: FOCUS_GROUPS_VS_SURVEYS_VS_IHUT_FAQ_ITEMS,
        published: true,
    },
    'in-home-product-testing/how-to-test-cpg-product-with-real-consumers-before-launch': {
        heading: 'How to Test a CPG Product with Real Consumers Before Launch',
        breadcrumbLabel: 'How to Test a CPG Product Before Launch',
        metaTitle: 'How to Test a New CPG Product With Real Consumers Before Launch',
        metaDescription: 'Learn how to test a CPG product with real consumers before launch, including research methods, recruitment, sample size, metrics, and common mistakes.',
        bannerImage: 'caseStudies/usecases/ihut/how-to-test-cpg-product-with-real-consumers-before-launch/how-to-test-cpg-product-with-real-consumers-before-launch-banner.png',
        listItems: [
            'Written by Milad Zabihi',
            '11 min read',
        ],
        faqItems: HOW_TO_TEST_CPG_PRODUCT_WITH_REAL_CONSUMERS_BEFORE_LAUNCH_FAQ_ITEMS,
        published: true,
    },
    'in-home-product-testing/how-many-participants-you-need-for-ihut': {
        heading: 'How Many Participants Do You Need for an IHUT Study?',
        breadcrumbLabel: 'How Many Participants Do You Need for an IHUT',
        metaTitle: 'How Many Participants Do You Need for an IHUT Study? | Peekage',
        metaDescription: 'Find out how many participants you need for an IHUT, with sample size guidelines for CPG product testing, research goals, and audience segments.',
        bannerImage: 'caseStudies/usecases/ihut/how-many-participants-you-need-for-ihut/how-many-participants-you-need-for-ihut-hero.png',
        listItems: [
            'Written by Milad Zabihi',
            '10 min read',
        ],
        faqItems: HOW_MANY_PARTICIPANTS_IHUT_FAQ_ITEMS,
    },
    'in-home-product-testing/how-to-interpret-ihut-reports': {
        heading: 'How to Interpret IHUT Reports and Turn Consumer Feedback Into Product Decisions',
        breadcrumbLabel: 'How to Interpret IHUT Reports',
        metaTitle: 'How to Interpret IHUT Reports for Better Product Decisions',
        metaDescription: 'Learn how to interpret IHUT reports, analyze consumer feedback and key metrics, and turn in-home product testing results into better product decisions.',
        bannerImage: 'caseStudies/usecases/ihut/how-to-interpret-ihut-reports/how-to-interpret-ihut-reports-banner-v2.png',
        listItems: [
            'Written by Milad Zabihi',
            '10 min read',
        ],
        faqItems: HOW_TO_INTERPRET_IHUT_REPORTS_FAQ_ITEMS,
    },
    'in-home-product-testing/how-much-ihut-costs': {
        heading: 'How Much Does IHUT Cost and How Can You Run an IHUT Study on a Budget?',
        breadcrumbLabel: 'How Much Does an IHUT Cost?',
        metaTitle: 'How Much Does an IHUT Cost? Pricing, ROI & Budget Tips',
        metaDescription: 'Learn how much an IHUT costs, what affects the budget, how to calculate ROI, and practical ways to run in-home product testing without overspending.',
        bannerImage: 'caseStudies/usecases/ihut/how-much-ihut-costs/how-much-ihut-costs-banner.png',
        listItems: [
            'Written by Milad Zabihi',
            '10 min read',
        ],
        faqItems: HOW_MUCH_IHUT_COSTS_FAQ_ITEMS,
    },
    'in-home-product-testing/how-to-send-cpg-product-prototypes': {
        heading: 'How to Send CPG Product Prototypes to Testers for an IHUT Study',
        breadcrumbLabel: 'How to Send CPG Product Prototypes for IHUT',
        metaTitle: 'How to Send CPG Product Prototypes for IHUT Study | Peekage',
        metaDescription: 'Learn how to package and ship CPG products for IHUT studies, manage deliveries, estimate shipping costs, and handle damaged or delayed products.',
        bannerImage: 'caseStudies/usecases/ihut/how-to-send-cpg-product-prototypes/how-to-send-cpg-product-prototypes-hero-v2.png',
        listItems: [
            'Written by Milad Zabihi',
            '10 min read',
        ],
        faqItems: HOW_TO_SEND_CPG_PRODUCT_PROTOTYPES_FAQ_ITEMS,
    },
    'in-home-product-testing/ihut-report-templates': {
        heading: 'IHUT Report Templates: How to Structure Your In-Home Usage Testing Results',
        breadcrumbLabel: 'IHUT Report Templates',
        metaTitle: 'IHUT Report Templates: How to Structure IHUT Results | Peekage',
        metaDescription: 'Learn how to structure an IHUT report with a practical template covering key findings, consumer feedback, product metrics, segment analysis, and recommendations.',
        bannerImage: 'caseStudies/usecases/ihut/ihut-report-templates/ihut-report-templates-banner.png',
        listItems: [
            'Written by Milad Zabihi',
            '18 min read',
        ],
        faqItems: IHUT_REPORT_TEMPLATES_FAQ_ITEMS,
    },
    'in-home-product-testing/methods-ihut': {
        heading: 'What Are the Different Methods of IHUT and How Do They Work?',
        breadcrumbLabel: 'Methods of IHUT',
        metaTitle: 'IHUT Testing Methods: How Each One Works | Peekage',
        metaDescription: 'Learn the six main IHUT testing methods, what each can measure, and how to choose the right method for your research goals.',
        bannerImage: 'caseStudies/usecases/ihut/methods-ihut/methods-ihut-banner.png',
        bannerAlt: 'Different Methods of IHUT',
        listItems: [
            'Written by Milad Zabihi',
            '13 min read',
        ],
        faqItems: METHODS_IHUT_FAQ_ITEMS,
    },
    'in-home-product-testing/how-to-build-targeted-consumer-list-for-ihut': {
        heading: 'How to Build a Targeted Consumer List for In-Home Usage Testing',
        breadcrumbLabel: 'Build a Targeted Consumer List for IHUT',
        metaTitle: 'How to Build a Targeted Consumer List for IHUT',
        metaDescription: 'Learn how to build a targeted IHUT consumer list using demographics, purchase behavior, screening criteria, quotas, and consumer testing platforms.',
        bannerImage: 'caseStudies/usecases/ihut/how-to-build-targeted-consumer-list-for-ihut/how-to-build-targeted-consumer-list-for-ihut-banner.png',
        listItems: [
            'Written by Milad Zabihi',
            '11 min read',
        ],
        faqItems: TARGETED_CONSUMER_LIST_IHUT_FAQ_ITEMS,
        published: true,
    },
    'in-home-product-testing/ihut-survey-questions': {
        heading: 'In-Home Usage Test Survey Questions to Ask Consumers',
        breadcrumbLabel: 'IHUT Survey Questions',
        metaTitle: 'In-Home Usage Test Survey Questions: 50+ Examples',
        metaDescription: '50+ IHUT survey questions for before, during, and after product use, plus examples by category and tips for collecting better consumer feedback.',
        bannerImage: 'caseStudies/usecases/ihut/ihut-survey-questions/ihut-survey-questions-banner-v2.png',
        listItems: [
            'Written by Milad Zabihi',
            '11 min read',
        ],
        faqItems: IHUT_SURVEY_QUESTIONS_FAQ_ITEMS,
        published: true,
    },
    'in-home-product-testing/what-consumer-drivers-brands-should-test-with-ihut': {
        heading: 'What Consumer Drivers Should Brands Test With IHUT?',
        breadcrumbLabel: 'What Consumer Drivers to Test With IHUT',
        metaTitle: 'What Consumer Drivers Should Brands Test With IHUT? | Peekage',
        metaDescription: 'Learn which consumer drivers to test with IHUT, from product performance and sensory experience to usability, value, packaging, and purchase intent.',
        bannerImage: 'caseStudies/usecases/ihut/what-consumer-drivers-brands-should-test-with-ihut/what-consumer-drivers-brands-should-test-with-ihut-banner.png',
        listItems: [
            'Written by Milad Zabihi',
            '13 min read',
        ],
        faqItems: WHAT_CONSUMER_DRIVERS_BRANDS_SHOULD_TEST_WITH_IHUT_FAQ_ITEMS,
        published: true,
    },
    'in-home-product-testing/how-to-run-ihut-for-pet-products': {
        heading: 'How to Run an IHUT Study for Pet Products',
        breadcrumbLabel: 'IHUT for Pet Products',
        metaTitle: 'How to Run an In-Home Usage Test for Pet Products',
        metaDescription: 'Learn how to run an IHUT for pet products, from recruiting pet owners and testing food, toys, and accessories to measuring product acceptance and satisfaction.',
        bannerImage: 'caseStudies/usecases/ihut/how-to-run-ihut-for-pet-products/how-to-run-ihut-for-pet-products-hero.png',
        listItems: [
            'Written by Milad Zabihi',
            '13 min read',
        ],
        faqItems: IHUT_FOR_PET_PRODUCTS_FAQ_ITEMS,
        published: true,
    },
    'in-home-product-testing/how-ihut-works-for-food-beverage-products': {
        heading: 'How In-Home Product Testing Works for Food and Beverage Products',
        breadcrumbLabel: 'IHUT for Food and Beverage',
        metaTitle: 'How In-Home Product Testing (IHUT) Works for Food & Beverage',
        metaDescription: 'Learn how IHUT works for food and beverage products, what you can test, how to run a study, and how to collect reliable consumer feedback at home.',
        bannerImage: 'caseStudies/usecases/ihut/how-ihut-works-for-food-beverage-products/how-ihut-works-for-food-beverage-products-hero.png',
        listItems: [
            'Written by Milad Zabihi',
            '13 min read',
        ],
        faqItems: IHUT_FOR_FOOD_AND_BEVERAGE_FAQ_ITEMS,
        published: true,
    },
    'in-home-product-testing/how-to-run-ihut-for-refrigerated-frozen-products': {
        heading: 'How to Run an In-Home Usage Test for Refrigerated and Frozen Products',
        breadcrumbLabel: 'IHUT for Refrigerated and Frozen Products',
        metaTitle: 'How to Run an IHUT for Frozen and Refrigerated Products',
        metaDescription: 'Learn how to run an in-home usage product test for a frozen food brand, from recruiting and shipping cold products to analyzing consumer feedback.',
        bannerImage: 'caseStudies/usecases/ihut/how-to-run-ihut-for-refrigerated-frozen-products/how-to-run-ihut-for-refrigerated-frozen-products-hero.png',
        bannerAlt: 'How to Run an In-Home Usage Test for Refrigerated and Frozen Products',
        listItems: [
            'Written by Milad Zabihi',
            '13 min read',
        ],
        faqItems: IHUT_FOR_REFRIGERATED_FROZEN_PRODUCTS_FAQ_ITEMS,
        published: true,
    },
    'in-home-product-testing/how-to-run-ihut-for-beauty-and-personal-care-products': {
        heading: 'How to Run an IHUT Study for Beauty and Personal Care Products',
        breadcrumbLabel: 'IHUT for Beauty and Personal Care Products',
        metaTitle: 'How to Run an IHUT for Beauty and Personal Care Products',
        metaDescription: 'Learn how to run an in-home usage test for beauty and personal care products, from recruiting participants and distributing products to analyzing consumer feedback.',
        bannerImage: 'caseStudies/usecases/ihut/how-to-run-ihut-for-beauty-and-personal-care-products/how-to-run-ihut-for-beauty-and-personal-care-products-hero.png',
        bannerAlt: 'How to Run an IHUT Study for Beauty and Personal Care Products',
        listItems: [
            'Written by Milad Zabihi',
            '12 min read',
        ],
        faqItems: IHUT_FOR_BEAUTY_PERSONAL_CARE_FAQ_ITEMS,
        published: true,
    },
    'in-home-product-testing/how-to-run-ihut-for-wellness-supplement-products': {
        heading: 'How to Run an IHUT for Wellness and Supplement Products',
        breadcrumbLabel: 'IHUT for Wellness and Supplement Products',
        metaTitle: 'How to Run an IHUT for Wellness and Supplement Products',
        metaDescription: 'Learn how to run an IHUT for wellness and supplement products, from participant recruitment and testing to consumer feedback and analysis.',
        bannerImage: 'caseStudies/usecases/ihut/how-to-run-ihut-for-wellness-supplement-products/how-to-run-ihut-for-wellness-supplement-products-hero.png',
        bannerAlt: 'How to Run an IHUT for Wellness and Supplement Products',
        listItems: [
            'Written by Milad Zabihi',
            '12 min read',
        ],
        faqItems: IHUT_FOR_WELLNESS_SUPPLEMENTS_FAQ_ITEMS,
        published: true,
    },
    'in-home-product-testing/how-to-run-ihut-for-baby-products': {
        heading: 'How to Plan an IHUT for Baby Products From Recruitment to Feedback',
        breadcrumbLabel: 'IHUT for Baby Products',
        metaTitle: 'How to Run an IHUT for Baby Products | Peekage',
        metaDescription: 'Learn how to run an IHUT for baby products, from recruiting parents and delivering test products to collecting feedback and analyzing results.',
        bannerImage: 'caseStudies/usecases/ihut/how-to-run-ihut-for-baby-products/how-to-run-ihut-for-baby-products-hero.png',
        bannerAlt: 'How to Plan an IHUT for Baby Products From Recruitment to Feedback',
        listItems: [
            'Written by Milad Zabihi',
            '12 min read',
        ],
        faqItems: IHUT_FOR_BABY_PRODUCTS_FAQ_ITEMS,
        published: true,
    },
}

export function hasCaseContentPageConfig(routeKey: string): boolean {
    return Object.prototype.hasOwnProperty.call(ROUTE_PAGE_CONFIGS, routeKey)
}

export function isCaseContentPagePublished(routeKey: string): boolean {
    if (!hasCaseContentPageConfig(routeKey)) {
        return false
    }
    return getCaseContentPageConfig(routeKey).published !== false
}

export function getCaseContentPageConfig(routeKey: string): CaseContentPageConfig {
    return {
        ...DEFAULT_CASE_CONTENT_PAGE_CONFIG,
        ...(ROUTE_PAGE_CONFIGS[routeKey] ?? {}),
    }
}
